// Mercapoli - API Client
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${API_URL}/api`;

// Crear instancia de axios con configuración base
const apiClient = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para agregar token de autenticación
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('mercapoli_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Interceptor para manejar errores
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('mercapoli_token');
      localStorage.removeItem('mercapoli_user');
      // Solo redirigir si no estamos ya en login
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// ==================== AUTH ====================
export const auth = {
  register: async (data) => {
    const response = await apiClient.post('/auth/register', data);
    return response.data;
  },
  
  login: async (credentials) => {
    const response = await apiClient.post('/auth/login', credentials);
    return response.data;
  },
  
  getMe: async () => {
    const response = await apiClient.get('/auth/me');
    return response.data;
  },
  
  updateProfile: async (data) => {
    const response = await apiClient.put('/auth/profile', data);
    return response.data;
  },
  
  // Password Recovery (Required for App Store)
  requestPasswordReset: async (email) => {
    const response = await apiClient.post('/auth/request-password-reset', { email });
    return response.data;
  },
  
  resetPassword: async (token, newPassword) => {
    const response = await apiClient.post('/auth/reset-password', { token, new_password: newPassword });
    return response.data;
  },
  
  // Account Deletion (Required for App Store)
  deleteAccount: async (password) => {
    const response = await apiClient.delete('/auth/delete-account', { data: { password } });
    return response.data;
  },
};

// ==================== CATEGORÍAS ====================
export const categorias = {
  getAll: async () => {
    const response = await apiClient.get('/categorias');
    return response.data;
  },
  
  create: async (data) => {
    const response = await apiClient.post('/categorias', data);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await apiClient.put(`/categorias/${id}`, data);
    return response.data;
  },
  
  delete: async (id) => {
    const response = await apiClient.delete(`/categorias/${id}`);
    return response.data;
  },
};

// ==================== RESTAURANTES ====================
export const restaurantes = {
  getAll: async (params = {}) => {
    const response = await apiClient.get('/restaurantes', { params });
    return response.data;
  },
  
  getById: async (id) => {
    const response = await apiClient.get(`/restaurantes/${id}`);
    return response.data;
  },
  
  create: async (data) => {
    const response = await apiClient.post('/restaurantes', data);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await apiClient.put(`/restaurantes/${id}`, data);
    return response.data;
  },
  
  getMenu: async (id) => {
    const response = await apiClient.get(`/restaurantes/${id}/menu`);
    return response.data;
  },
};

// ==================== PRODUCTOS ====================
export const productos = {
  getAll: async (params = {}) => {
    const response = await apiClient.get('/productos', { params });
    return response.data;
  },
  
  create: async (data) => {
    const response = await apiClient.post('/productos', data);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await apiClient.put(`/productos/${id}`, data);
    return response.data;
  },
  
  delete: async (id) => {
    const response = await apiClient.delete(`/productos/${id}`);
    return response.data;
  },
};

// ==================== PEDIDOS ====================
export const pedidos = {
  create: async (data) => {
    const response = await apiClient.post('/pedidos', data);
    return response.data;
  },
  
  getAll: async (params = {}) => {
    const response = await apiClient.get('/pedidos', { params });
    return response.data;
  },
  
  getById: async (id) => {
    const response = await apiClient.get(`/pedidos/${id}`);
    return response.data;
  },
  
  updateEstado: async (id, estado) => {
    const response = await apiClient.put(`/pedidos/${id}/estado`, { estado });
    return response.data;
  },
};

// ==================== DELIVERY ====================
export const delivery = {
  getPedidosDisponibles: async () => {
    const response = await apiClient.get('/delivery/pedidos-disponibles');
    return response.data;
  },
  
  aceptarPedido: async (id) => {
    const response = await apiClient.post(`/delivery/aceptar/${id}`);
    return response.data;
  },
  
  getGanancias: async () => {
    const response = await apiClient.get('/delivery/ganancias');
    return response.data;
  },
};

// ==================== FAVORITOS ====================
export const favoritos = {
  getAll: async () => {
    const response = await apiClient.get('/favoritos');
    return response.data;
  },
  
  add: async (restauranteId) => {
    const response = await apiClient.post(`/favoritos/${restauranteId}`);
    return response.data;
  },
  
  remove: async (restauranteId) => {
    const response = await apiClient.delete(`/favoritos/${restauranteId}`);
    return response.data;
  },
};

// ==================== NOTIFICACIONES ====================
export const notificaciones = {
  getAll: async () => {
    const response = await apiClient.get('/notificaciones');
    return response.data;
  },
  
  marcarLeida: async (id) => {
    const response = await apiClient.put(`/notificaciones/${id}/leer`);
    return response.data;
  },
};

// ==================== ZONAS ====================
export const zonas = {
  getAll: async (params = {}) => {
    const response = await apiClient.get('/zonas', { params });
    return response.data;
  },
  
  create: async (data) => {
    const response = await apiClient.post('/zonas', data);
    return response.data;
  },
};

// ==================== ADMIN ====================
export const admin = {
  getDashboard: async () => {
    const response = await apiClient.get('/admin/dashboard');
    return response.data;
  },
  
  getExecutiveDashboard: async (params = {}) => {
    const response = await apiClient.get('/admin/executive-dashboard', { params });
    return response.data;
  },
  
  getUsuarios: async (params = {}) => {
    const response = await apiClient.get('/admin/usuarios', { params });
    return response.data;
  },
  
  toggleUsuarioEstado: async (userId) => {
    const response = await apiClient.put(`/admin/usuarios/${userId}/estado`);
    return response.data;
  },
  
  // Restaurantes/Comercios CRUD
  createComercio: async (data) => {
    const response = await apiClient.post('/admin/restaurantes', data);
    return response.data;
  },
  
  createRestaurante: async (data) => {
    const response = await apiClient.post('/admin/restaurantes', data);
    return response.data;
  },
  
  updateComercio: async (id, data) => {
    const response = await apiClient.put(`/admin/restaurantes/${id}`, data);
    return response.data;
  },
  
  updateRestaurante: async (id, data) => {
    const response = await apiClient.put(`/admin/restaurantes/${id}`, data);
    return response.data;
  },
  
  deleteComercio: async (id) => {
    const response = await apiClient.delete(`/admin/restaurantes/${id}`);
    return response.data;
  },
  
  deleteRestaurante: async (id) => {
    const response = await apiClient.delete(`/admin/restaurantes/${id}`);
    return response.data;
  },
  
  getSucursales: async (restauranteId) => {
    const response = await apiClient.get(`/admin/restaurantes/${restauranteId}/sucursales`);
    return response.data;
  },
  
  // Tipos de Comercio CRUD
  getTiposComercio: async () => {
    const response = await apiClient.get('/tipos-comercio');
    return response.data;
  },
  
  createTipoComercio: async (data) => {
    const response = await apiClient.post('/tipos-comercio', data);
    return response.data;
  },
  
  updateTipoComercio: async (id, data) => {
    const response = await apiClient.put(`/tipos-comercio/${id}`, data);
    return response.data;
  },
  
  deleteTipoComercio: async (id) => {
    const response = await apiClient.delete(`/tipos-comercio/${id}`);
    return response.data;
  },
  
  // Riders CRUD (antes Repartidores)
  getRiders: async (params = {}) => {
    const response = await apiClient.get('/admin/repartidores', { params });
    return response.data;
  },
  
  createRider: async (data) => {
    const response = await apiClient.post('/admin/repartidores', data);
    return response.data;
  },
  
  updateRider: async (id, data) => {
    const response = await apiClient.put(`/admin/repartidores/${id}`, data);
    return response.data;
  },
  
  deleteRider: async (id) => {
    const response = await apiClient.delete(`/admin/repartidores/${id}`);
    return response.data;
  },
  
  // Repartidores CRUD (alias para compatibilidad)
  getRepartidores: async (params = {}) => {
    const response = await apiClient.get('/admin/repartidores', { params });
    return response.data;
  },
  
  createRepartidor: async (data) => {
    const response = await apiClient.post('/admin/repartidores', data);
    return response.data;
  },
  
  updateRepartidor: async (id, data) => {
    const response = await apiClient.put(`/admin/repartidores/${id}`, data);
    return response.data;
  },
  
  // Finanzas
  getResumenFinanciero: async (params = {}) => {
    const response = await apiClient.get('/admin/finanzas/resumen', { params });
    return response.data;
  },
  
  // Finance Dashboard Avanzado (Fase 3)
  getFinanceDashboard: async (params = {}) => {
    const response = await apiClient.get('/admin/finance-dashboard', { params });
    return response.data;
  },
  
  getBalanceRepartidores: async () => {
    const response = await apiClient.get('/admin/finanzas/repartidores');
    return response.data;
  },
  
  getBalanceRestaurantes: async () => {
    const response = await apiClient.get('/admin/finanzas/restaurantes');
    return response.data;
  },
  
  pagarRepartidor: async (data) => {
    const response = await apiClient.post('/admin/finanzas/pagar-repartidor', data);
    return response.data;
  },
  
  pagarRestaurante: async (data) => {
    const response = await apiClient.post('/admin/finanzas/pagar-restaurante', data);
    return response.data;
  },
  
  // Exportación de reportes (Fase 3 - mejorado)
  exportarExcel: (periodo = 'mes') => {
    const token = localStorage.getItem('mercapoli_token');
    return `${API}/admin/finanzas/exportar/excel?periodo=${periodo}&token=${token}`;
  },
  
  exportarPdf: (periodo = 'mes') => {
    const token = localStorage.getItem('mercapoli_token');
    return `${API}/admin/finanzas/exportar/pdf?periodo=${periodo}&token=${token}`;
  },
  
  // Exportación Finance Dashboard (Fase 3)
  exportFinanceExcel: (params = {}) => {
    const token = localStorage.getItem('mercapoli_token');
    const queryParams = new URLSearchParams({ ...params, token }).toString();
    return `${API}/admin/finance-dashboard/export/excel?${queryParams}`;
  },
  
  exportFinancePdf: (params = {}) => {
    const token = localStorage.getItem('mercapoli_token');
    const queryParams = new URLSearchParams({ ...params, token }).toString();
    return `${API}/admin/finance-dashboard/export/pdf?${queryParams}`;
  },
};

// ==================== CONFIG ====================
export const config = {
  getPaises: async () => {
    const response = await apiClient.get('/config/paises');
    return response.data;
  },
  
  getPais: async (codigo) => {
    const response = await apiClient.get(`/config/pais/${codigo}`);
    return response.data;
  },
};

// ==================== CIUDADES ====================
export const ciudades = {
  getAll: async (pais = null) => {
    const params = pais ? { pais } : {};
    const response = await apiClient.get('/ciudades', { params });
    return response.data;
  },
  
  getById: async (ciudadId) => {
    const response = await apiClient.get(`/ciudades/${ciudadId}`);
    return response.data;
  },
  
  // Admin
  create: async (data) => {
    const response = await apiClient.post('/admin/ciudades', data);
    return response.data;
  },
  
  update: async (ciudadId, data) => {
    const response = await apiClient.put(`/admin/ciudades/${ciudadId}`, data);
    return response.data;
  },
  
  deactivate: async (ciudadId) => {
    const response = await apiClient.delete(`/admin/ciudades/${ciudadId}`);
    return response.data;
  },
};

// ==================== CUPONES ====================
export const cupones = {
  getAll: async (params = {}) => {
    const response = await apiClient.get('/cupones', { params });
    return response.data;
  },
  
  create: async (data) => {
    const response = await apiClient.post('/cupones', data);
    return response.data;
  },
  
  validar: async (data) => {
    const response = await apiClient.post('/cupones/validar', data);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await apiClient.put(`/cupones/${id}`, data);
    return response.data;
  },
  
  delete: async (id) => {
    const response = await apiClient.delete(`/cupones/${id}`);
    return response.data;
  },
};

// ==================== DIGGII ====================
export const diggii = {
  // Usuario
  getBalance: async () => {
    const response = await apiClient.get('/diggii/balance');
    return response.data;
  },
  
  getHistorial: async (params = {}) => {
    const response = await apiClient.get('/diggii/historial', { params });
    return response.data;
  },
  
  calcularCanje: async (data) => {
    const response = await apiClient.post('/diggii/calcular-canje', data);
    return response.data;
  },
  
  getConfig: async () => {
    const response = await apiClient.get('/diggii/config');
    return response.data;
  },
  
  // Admin
  getResumenAdmin: async () => {
    const response = await apiClient.get('/admin/diggii/resumen');
    return response.data;
  },
  
  getUsuariosAdmin: async (params = {}) => {
    const response = await apiClient.get('/admin/diggii/usuarios', { params });
    return response.data;
  },
  
  crearAjuste: async (data) => {
    const response = await apiClient.post('/admin/diggii/ajuste', data);
    return response.data;
  },
  
  // Tipos de cambio
  getTiposCambio: async () => {
    const response = await apiClient.get('/admin/tipos-cambio');
    return response.data;
  },
  
  updateTipoCambio: async (pais, tasa_usd) => {
    const response = await apiClient.put(`/admin/tipos-cambio/${pais}`, null, {
      params: { tasa_usd }
    });
    return response.data;
  },
  
  getTipoCambioPais: async (pais) => {
    const response = await apiClient.get(`/tipos-cambio/${pais}`);
    return response.data;
  },
};

// ==================== SEED ====================
export const seed = async () => {
  const response = await apiClient.post('/seed');
  return response.data;
};

// ==================== TARIFA ENTREGA ====================
export const tarifaEntrega = {
  calcular: async (distancia_km, pais = 'GT') => {
    const response = await apiClient.get('/tarifa-entrega/calcular', {
      params: { distancia_km, pais }
    });
    return response.data;
  },
  
  getConfig: async () => {
    const response = await apiClient.get('/tarifa-entrega/config');
    return response.data;
  }
};

// ==================== RIDER ====================
export const rider = {
  getDashboard: async () => {
    const response = await apiClient.get('/rider/dashboard');
    return response.data;
  },
  
  getPedidos: async (params = {}) => {
    const response = await apiClient.get('/rider/pedidos', { params });
    return response.data;
  },
  
  getPedidosDisponibles: async () => {
    const response = await apiClient.get('/rider/pedidos-disponibles');
    return response.data;
  }
};

// ==================== COMERCIO ====================
export const comercio = {
  getDashboard: async () => {
    const response = await apiClient.get('/comercio/dashboard');
    return response.data;
  },
  
  getPedidos: async (params = {}) => {
    const response = await apiClient.get('/comercio/pedidos', { params });
    return response.data;
  },
  
  aceptarComision: async () => {
    const response = await apiClient.put('/comercio/aceptar-comision');
    return response.data;
  },
  
  getPlanes: async () => {
    const response = await apiClient.get('/comercio/planes');
    return response.data;
  },
  
  // === PRODUCTOS (Módulo Comercio) ===
  getProductos: async (params = {}) => {
    const response = await apiClient.get('/comercio/productos', { params });
    return response.data;
  },
  
  createProducto: async (data) => {
    const response = await apiClient.post('/comercio/productos', data);
    return response.data;
  },
  
  updateProducto: async (id, data) => {
    const response = await apiClient.put(`/comercio/productos/${id}`, data);
    return response.data;
  },
  
  deleteProducto: async (id) => {
    const response = await apiClient.delete(`/comercio/productos/${id}`);
    return response.data;
  },
  
  cambiarEstadoProducto: async (id, estado) => {
    const response = await apiClient.put(`/comercio/productos/${id}/estado?estado=${estado}`);
    return response.data;
  },
  
  subirImagenProducto: async (productoId, file, esPrincipal = false) => {
    const formData = new FormData();
    formData.append('archivo', file);
    const response = await apiClient.post(
      `/comercio/productos/${productoId}/imagenes?es_principal=${esPrincipal}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },
  
  eliminarImagenProducto: async (productoId, url) => {
    const response = await apiClient.delete(`/comercio/productos/${productoId}/imagenes?url=${encodeURIComponent(url)}`);
    return response.data;
  },
  
  // === PROMOCIONES ===
  getPromociones: async (params = {}) => {
    const response = await apiClient.get('/comercio/promociones', { params });
    return response.data;
  },
  
  createPromocion: async (data) => {
    const response = await apiClient.post('/comercio/promociones', data);
    return response.data;
  },
  
  togglePromocion: async (id, activa) => {
    const response = await apiClient.put(`/comercio/promociones/${id}?activa=${activa}`);
    return response.data;
  },
  
  deletePromocion: async (id) => {
    const response = await apiClient.delete(`/comercio/promociones/${id}`);
    return response.data;
  },
  
  // === REVIEWS ===
  getReviews: async (params = {}) => {
    const response = await apiClient.get('/comercio/reviews', { params });
    return response.data;
  },
  
  responderReview: async (id, respuesta) => {
    const response = await apiClient.post(`/comercio/reviews/${id}/responder`, { respuesta });
    return response.data;
  },
  
  reportarReview: async (id, motivo) => {
    const response = await apiClient.post(`/comercio/reviews/${id}/reportar?motivo=${encodeURIComponent(motivo)}`);
    return response.data;
  },
  
  // === PERFIL DEL COMERCIO ===
  getPerfil: async () => {
    const response = await apiClient.get('/comercio/perfil');
    return response.data;
  },
  
  updatePerfil: async (data) => {
    const params = new URLSearchParams();
    Object.entries(data).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        params.append(key, value);
      }
    });
    const response = await apiClient.put(`/comercio/perfil?${params.toString()}`);
    return response.data;
  },
  
  // === CATEGORÍAS ===
  getCategorias: async () => {
    const response = await apiClient.get('/comercio/categorias');
    return response.data;
  },
  
  createCategoria: async (nombre, descripcion = null) => {
    const response = await apiClient.post(`/comercio/categorias?nombre=${encodeURIComponent(nombre)}${descripcion ? `&descripcion=${encodeURIComponent(descripcion)}` : ''}`);
    return response.data;
  },
  
  // === ESTADÍSTICAS ===
  getEstadisticas: async () => {
    const response = await apiClient.get('/comercio/estadisticas');
    return response.data;
  },
  
  // === FINANZAS ===
  getResumenVentas: async (periodo = 'semana') => {
    const response = await apiClient.get(`/comercio/finanzas/ventas/resumen?periodo=${periodo}`);
    return response.data;
  },
  
  getDetalleVentas: async (fechaInicio, fechaFin) => {
    const response = await apiClient.get(`/comercio/finanzas/ventas/detalle?fecha_inicio=${fechaInicio}&fecha_fin=${fechaFin}`);
    return response.data;
  },
  
  getLiquidaciones: async (params = {}) => {
    const response = await apiClient.get('/comercio/finanzas/liquidaciones', { params });
    return response.data;
  },
  
  descargarLiquidacionPdf: (liquidacionId) => {
    const token = localStorage.getItem('mercapoli_token');
    return `${API_URL}/api/comercio/finanzas/liquidaciones/${liquidacionId}/recibo?token=${token}`;
  }
};

// ==================== HEALTH ====================
export const health = async () => {
  const response = await apiClient.get('/health');
  return response.data;
};

// ==================== PAYMENTS (Stripe Checkout) ====================
export const payments = {
  createCheckoutSession: async (pedido_id, origin_url) => {
    const response = await apiClient.post('/payments/create-checkout', {
      pedido_id,
      origin_url
    });
    return response.data;
  },
  
  getPaymentStatus: async (session_id) => {
    const response = await apiClient.get(`/payments/status/${session_id}`);
    return response.data;
  },
  
  // === MERCADOPAGO ===
  getMercadoPagoConfig: async () => {
    const response = await apiClient.get('/payments/mercadopago/config');
    return response.data;
  },
  
  createMercadoPagoPreference: async (pedido_id, descripcion, monto, moneda = 'GTQ') => {
    const response = await apiClient.post('/payments/mercadopago/create-preference', {
      pedido_id,
      descripcion,
      monto,
      moneda
    });
    return response.data;
  },
  
  getMercadoPagoPaymentStatus: async (pedido_id) => {
    const response = await apiClient.get(`/payments/mercadopago/pedido/${pedido_id}/status`);
    return response.data;
  }
};

// ==================== MAPS ====================
export const maps = {
  calculateDistance: async (origen_lat, origen_lon, destino_lat, destino_lon) => {
    const response = await apiClient.post('/maps/distance', {
      origen_lat, origen_lon, destino_lat, destino_lon
    });
    return response.data;
  },
  
  getRestaurantesCercanos: async (lat, lon, radio_km = 5) => {
    const response = await apiClient.get('/maps/restaurantes-cercanos', {
      params: { lat, lon, radio_km }
    });
    return response.data;
  }
};

// ==================== TRACKING ====================
export const tracking = {
  updateRiderUbicacion: async (latitud, longitud, pedido_id = null) => {
    const response = await apiClient.post('/tracking/rider/ubicacion', {
      latitud, longitud, pedido_id
    });
    return response.data;
  },
  
  getTrackingPedido: async (pedido_id) => {
    const response = await apiClient.get(`/tracking/pedido/${pedido_id}`);
    return response.data;
  },
  
  getRiderPedidoActivo: async () => {
    const response = await apiClient.get('/tracking/rider/pedido-activo');
    return response.data;
  }
};

// ==================== NOTIFICATIONS ====================
export const notifications = {
  registerToken: async (fcm_token) => {
    const response = await apiClient.post('/notifications/register-token', { fcm_token });
    return response.data;
  },
  
  unregisterToken: async () => {
    const response = await apiClient.delete('/notifications/unregister-token');
    return response.data;
  },
  
  getAll: async (limit = 20) => {
    const response = await apiClient.get(`/notifications?limit=${limit}`);
    return response.data;
  },
  
  markAsRead: async (notificacion_id) => {
    const response = await apiClient.put(`/notifications/${notificacion_id}/read`);
    return response.data;
  },
  
  markAllAsRead: async () => {
    const response = await apiClient.put('/notifications/read-all');
    return response.data;
  }
};

export default apiClient;
